import { forwardRef, HTMLAttributes, useEffect, useRef, useState } from 'react'
import { useClickAway, useKey } from 'react-use'
import gsap from 'gsap'
import Lenis from 'lenis'
import Image from 'next/image'
import Link from 'next/link'

import Button from '@/components/base/button'
import Typography from '@/components/base/typography'
import TypographyMask from '@/components/base/typography-mask'
import { useScrollbar } from '@/contexts/lenis'
import { CloseButton, ModalActions } from '@/contexts/modal'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'

export type BannerProps = HTMLAttributes<HTMLDivElement> & ModalActions

const BannerModal = forwardRef<HTMLDivElement, BannerProps>(
  ({ onClose, className, ...props }, ref) => {
    const { strings } = useStore()
    const { labels, modals } = strings || {}
    const { banner } = modals || {}
    const [lenis, setLenis] = useState<Lenis>()
    const [track, thumb, needsScrollbar] = useScrollbar(lenis)
    const wrapper = useRef<HTMLDivElement>(null!)
    const content = useRef<HTMLDivElement>(null!)

    useClickAway(wrapper, () => onClose?.())

    useKey('Escape', onClose)

    useEffect(() => {
      const lenis = new Lenis({
        smoothWheel: true,
        wrapper: wrapper.current,
        content: content.current
      })

      const tick = (time: number) => {
        lenis.raf(time * 1000)
      }

      gsap.ticker.add(tick)

      setLenis(lenis)

      return () => {
        gsap.ticker.remove(tick)
        lenis.destroy()
        setLenis(undefined)
      }
    }, [])

    return (
      <div
        ref={ref}
        className={cn(
          'fixed left-0 top-0 z-modal h-full w-full bg-black/30',
          'flex p-gutter',
          className
        )}
        data-lenis-prevent
        {...props}
      >
        <div
          ref={wrapper}
          className={cn(
            'relative m-auto h-auto max-h-full w-full overflow-auto rounded-[0.5rem] bg-white scrollbar-hide',
            'max-w-full lg:max-w-[74.5rem] xl:max-w-[81.5rem]'
          )}
        >
          <CloseButton
            onClick={onClose}
            className="absolute right-[1.5rem] top-[1.5rem] z-10 lg:right-[3rem] lg:top-[3rem]"
          >
            {labels?.close}
          </CloseButton>

          <div
            ref={content}
            className="flex h-auto w-full flex-col md:flex-row"
          >
            <div
              className={cn(
                'relative aspect-[408/350] md:aspect-[408/450]',
                'shrink-0 basis-1/2'
              )}
            >
              <Image
                src="/assets/image-04.jpg"
                alt=""
                fill
                className="rounded-t-[0.5rem] object-cover md:rounded-l-[0.5rem] md:rounded-t-[0rem]"
              />
            </div>

            <div
              className={cn(
                'my-auto flex shrink-0 basis-1/2 p-gutter text-center',
                'grid gap-y-[1.5rem]'
              )}
            >
              <Typography variant="h6" component="h6" className="font-timesnow">
                {banner?.title}
              </Typography>

              <Typography
                variant="body"
                paragraph
                className="font-gillsans font-light"
              >
                {banner?.paragraph}
              </Typography>

              <div>
                <Button asChild>
                  <Link href={banner?.link.url} target={banner?.link.target}>
                    <TypographyMask>
                      <Typography variant="label">
                        {banner?.link.title}
                      </Typography>
                    </TypographyMask>
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div
          className={cn(
            'group absolute bottom-0 right-0 top-0 z-scrollbar hidden w-[1.2rem]',
            {
              visible: needsScrollbar,
              invisible: !needsScrollbar
            }
          )}
        >
          <div ref={track} className="relative h-full w-full">
            <div
              ref={thumb}
              className={cn(
                'absolute left-0 top-0 w-full',
                'before:absolute before:bottom-[.2rem] before:left-[.4rem] before:top-[.2rem] before:w-[.6rem] before:rounded-full before:bg-havana'
              )}
            />
          </div>
        </div>
      </div>
    )
  }
)

export default BannerModal
